import { useCallback, useState, type ComponentProps } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button/new';
import FeedbackCollector from '@atlaskit/feedback-collector';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';

import navigationUrls from 'src/components/navigation/src/urls';
import { useAnalytics } from 'src/components/settings/analytics';
import { showFlagComponent } from 'src/redux/flags';
import { getCurrentUserEmail } from 'src/selectors/global-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { urls as workspaceUrls } from 'src/urls/workspaces';

import {
  FEEDBACK_CONTEXT_CUSTOM_FIELD_ID,
  FEEDBACK_COLLECTOR_ENTRYPOINT_IDS,
  buildFeedbackContextValue,
  FeedbackContextLabel,
} from './constants';
import { messages } from './feedback-collector.i18n';

export type FeedbackCollectorProps = {
  feedbackContextLabel: FeedbackContextLabel;
  onSubmit?: () => void;
  timeoutOnSubmit?: number;
  summaryPlaceholder?: string;
  renderTrigger?: (onClick: () => void) => JSX.Element;
} & Omit<ComponentProps<typeof Button>, 'children' | 'iconBefore' | 'onClick'>;

export const FeedbackCollectorComponent = ({
  feedbackContextLabel,
  onSubmit,
  timeoutOnSubmit,
  summaryPlaceholder,
  renderTrigger,
  ...buttonProps
}: FeedbackCollectorProps): JSX.Element => {
  const { publishButtonClickEvent } = useAnalytics({
    source: feedbackContextLabel,
  });
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const currentUserEmail = useSelector(getCurrentUserEmail);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSubmit = useCallback(() => {
    publishButtonClickEvent('sendFeedback');
    onSubmit?.();
    setIsOpen(false);
    dispatch(showFlagComponent('pipelines-feedback-button'));
  }, [dispatch, onSubmit, publishButtonClickEvent]);

  const entrypointId = FEEDBACK_COLLECTOR_ENTRYPOINT_IDS[feedbackContextLabel];

  const handleOpen = useCallback(() => {
    publishButtonClickEvent('giveFeedback');
    setIsOpen(true);
  }, [publishButtonClickEvent]);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <Button
          appearance="subtle"
          iconBefore={MegaphoneIcon}
          onClick={handleOpen}
          {...buttonProps}
        >
          {intl.formatMessage(messages.giveFeedback)}
        </Button>
      )}
      {isOpen && entrypointId ? (
        <FeedbackCollector
          locale={intl.locale}
          entrypointId={entrypointId}
          atlassianAccountId={currentUser?.account_id}
          name={currentUser?.display_name}
          email={currentUserEmail || undefined}
          onClose={handleClose}
          onSubmit={handleSubmit}
          {...(timeoutOnSubmit !== undefined ? { timeoutOnSubmit } : {})}
          additionalFields={[
            {
              id: FEEDBACK_CONTEXT_CUSTOM_FIELD_ID,
              value: buildFeedbackContextValue(feedbackContextLabel),
            },
          ]}
          showTypeField={false}
          feedbackTitle={intl.formatMessage(messages.giveFeedback)}
          feedbackTitleDetails={
            <>
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsOne}
                tagName="p"
              />
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsTwo}
                values={{
                  link: chunks => (
                    <a
                      href={workspaceUrls.external.contactSupportLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {chunks}
                    </a>
                  ),
                }}
                tagName="p"
              />
            </>
          }
          canBeContactedLabel={
            <FormattedMessage
              {...messages.canContactLabel}
              values={{
                link: chunks => (
                  <a
                    href={navigationUrls.external.privacyPolicy()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
              tagName="p"
            />
          }
          enrolInResearchLabel={
            <FormattedMessage
              {...messages.enrollLabel}
              values={{
                link: chunks => (
                  <a
                    href={navigationUrls.external.atlassianResearchGroup()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
              tagName="p"
            />
          }
          summaryPlaceholder={
            summaryPlaceholder ??
            intl.formatMessage(messages.giveFeedbackPlaceholder)
          }
        />
      ) : null}
    </>
  );
};
